import { Button, ButtonProps, FormControlLabel, Grid, Switch } from '@mui/material'
import React from 'react'
import { Controller, useFormContext as useHookFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useFormikContext } from 'formik'
import DefaultSpacingGrid from '../Containers/DefaultSpacingGrid'

function useSafeFormikContext() {
  try {
    const context = useFormikContext<any>()
    if (context === undefined) {
      return null
    }
    return context
  } catch {
    return null
  }
}

interface SubmitButtonsProps {
  create?: boolean
  isEnabled?: boolean
  createButtonText?: string
  updateButtonText?: string
  cancelButtonText?: string
  submitForm?: () => void
  onCancel?: () => void
  buttonSize?: ButtonProps['size']
  enableName?: string
  showEnabledSwitch?: boolean
}

const SubmitButtons = ({
  create = true,
  isEnabled = true,
  createButtonText,
  updateButtonText,
  cancelButtonText,
  submitForm,
  onCancel,
  buttonSize,
  enableName = 'active',
  showEnabledSwitch = false
}: SubmitButtonsProps) => {
  const oldContext = useSafeFormikContext()

  const hookFormContext = useHookFormContext()

  const [enabled, setEnabled] = React.useState(isEnabled)

  const handleSubmit = () => {
    if (submitForm) {
      submitForm()
    } else if (oldContext && oldContext.submitForm) {
      oldContext.submitForm()
    } else if (hookFormContext && hookFormContext.handleSubmit) {
      // React Hook Form's handleSubmit requires a submit handler callback
      // This triggers form validation and submission through the form's onSubmit
      hookFormContext.handleSubmit((data) => data)()
    }
  }
  return (
    <DefaultSpacingGrid container justifyContent='flex-start' sx={{ mt: 0 }}>
      <Grid item>
        <Button variant='outlined' onClick={onCancel} size={buttonSize}>
          <FormattedMessage id='common.cancel' defaultMessage={cancelButtonText || 'Cancel'} />
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant='contained'
          onClick={handleSubmit}
          size={buttonSize}
        >
          {create ? (
            <FormattedMessage id='common.create' defaultMessage={createButtonText || 'Create'} />
          ) : (
            <FormattedMessage id='common.save' defaultMessage={updateButtonText || 'Save'} />
          )}
        </Button>
      </Grid>
      {showEnabledSwitch && enableName && (
        <Grid item >
          <Controller
            name={enableName}
            control={hookFormContext?.control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value ?? enabled}
                    onChange={(e) => {
                      field.onChange(e.target.checked)
                      setEnabled(e.target.checked)
                    }}
                  />
                }
                label={<FormattedMessage id='common.enabled' defaultMessage='Enabled' />}
              />
            )}
          />
        </Grid>
      )}
    </DefaultSpacingGrid>
  )
}

export default SubmitButtons
